import * as Sentry from '@sentry/react';
import { isKiosk } from 'utils/platform';
import { getQuery } from 'utils/queryUtils';
const forceEnableSentry = getQuery().get('forceEnableSentry') === 'true';

const autoDetectEnvironment = (): string => {
  if (window && window.location) {
    return window.location.hostname;
  }

  return 'Unknown';
};

const init = (
  release: string,
  initOptions?: Sentry.BrowserOptions,
  sentryReplayLoggingEnabled?: boolean
): void => {
  if (forceEnableSentry) {
    sentryReplayLoggingEnabled = true;
  }

  Sentry.init({
    ...initOptions,
    dsn: 'https://ac9c160622ba421d8602dcdc331096d5@o514642.ingest.sentry.io/4504893461626880',
    release,
    environment: autoDetectEnvironment(),
    // No override used here as this won't be used if the feature is not enabled
    replaysSessionSampleRate: forceEnableSentry ? 1 : isKiosk() ? 0.01 : 0,
    replaysOnErrorSampleRate: forceEnableSentry ? 1 : 0.005,
    integrations: sentryReplayLoggingEnabled
      ? [
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: true,
            maskAllInputs: true,
            block: ['kompis-keyboard'],
            unblock: ['.sentry-unblock, [data-sentry-unblock]'],
            unmask: ['.sentry-unmask, [data-sentry-unmask]']
          }),
          Sentry.replayCanvasIntegration({
            quality: 'low'
          })
        ]
      : [],
    beforeSend(event, hint) {
      // Skip tags.mechanism = onunhandledrejection
      // Ignore errors like PAX-PLANNER-EVQ (failed to fetch)
      if (event.tags?.mechanism === 'onunhandledrejection') {
        return null;
      }

      if (
        hint.originalException instanceof Error &&
        hint.originalException.message.match(/Failed to send.*/)
      ) {
        return null;
      }

      // THis is for testing if the error is caught as the ignoreErrors list is not working properly (or it's the wrong format)
      if (
        hint.originalException instanceof Error &&
        hint.originalException.message.match(/^Error: Should not already be working/)
      ) {
        return null;
      }

      return event;
    },
    // only catch errors originating from localhost, ikea.com or ikeadt.com
    allowUrls: [/.*(localhost|ikea\.com|ikeadt\.com).*/],
    // Declutter sentry by ignoring common errors not triggered by app itself
    // @see https://docs.sentry.io/platforms/javascript/#decluttering-sentry
    ignoreErrors: [
      // Random plugins/extensions
      'top.GLOBALS',
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',
      // Facebook borked
      'fb_xd_fragment',
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      'conduitPage',
      // Error generated by a bug in auto-fill library from browser
      // https://github.com/getsentry/sentry/issues/5267
      /Blocked a frame with origin/,
      'a[b].target.className.indexOf', // If user have Google Translate plugin installed in the browser this error sometimes occurs
      'Failed to fetch',
      /^Error: Should not already be working/,
      'WebGL not supported',
      'Unable to create vertex buffer',
      'Unable to create texture',
      'Unable to create index buffer',
      'ConnectionProblemException',
      'is set to "disabled"',
      'Failed to retrieve zip code API',
      'Failed to retrieve store API',
      'Failed to send navigation event, TypeError: Load failed',
      'Viewer: camera is locked! Camera can not be changed during drag, wait until drag_end',
      /^Error: Failed to send.*, TypeError$/,
      'Failed to load iframe. Request timed out. Connection problems?',
      'Undefined assets for url',
      /Failed to send.*/, // Failed to send navigation event, TypeError: Load failed,
      'Object does not exist',
      'Error: Object does not exist',
      'Unable to create VAO'
    ]
  });
};

export default init;
